import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth, useUsageStore } from '../../../stores';

const THEME_COLOR = '#c59253';

const SpaceCreation = ({ state, updateState }) => {
    const { getLimitInfo } = useUsageStore();
    const [expandedSpaces, setExpandedSpaces] = useState({});

    const sections = state.sections || [];
    const spaces = state.spaces || [];
    const parentSpaces = spaces.filter(s => !s.parentId);
    const subSpaces = spaces.filter(s => s.parentId);

    const spaceLimit = getLimitInfo("spaces");
    const subSpaceLimit = getLimitInfo("sub_spaces");

    const isMaxSpacesReached = spaceLimit.isReached || (spaceLimit.limit !== null && (spaceLimit.current + parentSpaces.length) > spaceLimit.limit);
    const isMaxSubSpacesReached = subSpaceLimit.isReached || (subSpaceLimit.limit !== null && (subSpaceLimit.current + subSpaces.length) > subSpaceLimit.limit);

    const addSpace = (parentId = null) => {
        // Check limits before adding
        if (!parentId && isMaxSpacesReached) return;
        if (parentId && isMaxSubSpacesReached) return;

        // Count existing spaces at this level
        const siblingsCount = spaces.filter(s => s.parentId === parentId).length;

        const newSpace = {
            id: Date.now().toString(),
            name: parentId ? `Subspace ${siblingsCount + 1}` : `Space ${siblingsCount + 1}`,
            type: 'space',
            parentId: parentId,
            selectedSections: [], // NEW: User selects sections here
            sectionOrder: [], // Will be populated in section ordering step
        };
        updateState({ spaces: [...spaces, newSpace] });

        // Auto-expand parent if adding subspace
        if (parentId) {
            setExpandedSpaces(prev => ({ ...prev, [parentId]: true }));
        }
    };

    const deleteSpace = (id) => {
        // Also delete all children
        const toDelete = [id];
        const findChildren = (parentId) => {
            spaces.filter(s => s.parentId === parentId).forEach(child => {
                toDelete.push(child.id);
                findChildren(child.id);
            });
        };
        findChildren(id);

        updateState({
            spaces: spaces.filter((s) => !toDelete.includes(s.id)),
        });
    };

    const updateSpace = (id, field, value) => {
        updateState({
            spaces: spaces.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
        });
    };

    const toggleExpand = (id) => {
        setExpandedSpaces(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleSection = (spaceId, sectionId) => {
        const space = (state.spaces || []).find(s => s.id === spaceId);
        if (!space) return;

        const selectedSections = space.selectedSections || [];
        const isSelected = selectedSections.includes(sectionId);

        const newSelectedSections = isSelected
            ? selectedSections.filter(id => id !== sectionId)
            : [...selectedSections, sectionId];

        // If the section was removed, also remove it from sectionOrder
        let newSectionOrder = space.sectionOrder || [];
        if (isSelected) {
            newSectionOrder = newSectionOrder.filter(id => id !== sectionId);
        } else {
            // If it was added, append it to the end of the order
            newSectionOrder = [...newSectionOrder, sectionId];
        }

        updateState({
            spaces: (state.spaces || []).map((s) => (s.id === spaceId ? {
                ...s,
                selectedSections: newSelectedSections,
                sectionOrder: newSectionOrder
            } : s)),
        });
    };

    const selectAllSections = (spaceId) => {
        const allSectionIds = (sections || []).map(s => s.id);
        updateState({
            spaces: (state.spaces || []).map((s) => (s.id === spaceId ? {
                ...s,
                selectedSections: allSectionIds,
                sectionOrder: allSectionIds
            } : s)),
        });
    };

    const clearAllSections = (spaceId) => {
        updateState({
            spaces: (state.spaces || []).map((s) => (s.id === spaceId ? {
                ...s,
                selectedSections: [],
                sectionOrder: []
            } : s)),
        });
    };

    const renderSpace = (space, level = 0) => {
        const children = spaces.filter(s => s.parentId === space.id);
        const isExpanded = expandedSpaces[space.id];
        const isParent = level === 0;
        const selectedSections = space.selectedSections || [];

        return (
            <div key={space.id} style={{ marginLeft: `${level * 24}px` }} className="mb-3">
                <div className="card border">
                    <div className="card-body p-3">
                        {/* Space Name & Actions */}
                        <div className="d-flex align-items-center gap-3 mb-3">
                            {/* Expand/Collapse for parent spaces with children */}
                            {isParent && children.length > 0 && (
                                <button
                                    onClick={() => toggleExpand(space.id)}
                                    className="btn btn-link btn-sm p-0 text-muted"
                                >
                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </button>
                            )}

                            <div className="flex-grow-1">
                                <div className="row g-2 mb-1">
                                    <div className="col-12 col-md-6">
                                        <input
                                            type="text"
                                            value={space.name}
                                            onChange={(e) => updateSpace(space.id, 'name', e.target.value)}
                                            className="form-control form-control-sm fw-bold border-0 p-0 shadow-none"
                                            placeholder={isParent ? "Space name" : "Subspace name"}
                                            style={{ fontSize: '1.1rem' }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <input
                                            type="text"
                                            value={space.nameAr || ''}
                                            onChange={(e) => updateSpace(space.id, 'nameAr', e.target.value)}
                                            className="form-control form-control-sm border-0 p-0 shadow-none text-md-end text-muted"
                                            placeholder="الإسم بالعربية"
                                            style={{ direction: 'rtl' }}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="badge bg-light text-muted fw-normal" style={{ fontSize: '0.7rem' }}>
                                        {isParent ? 'PARENT SPACE' : 'SUBSPACE'}
                                    </span>
                                </div>
                            </div>

                            <div className="d-flex gap-2">
                                {isParent && (
                                    <button
                                        onClick={() => addSpace(space.id)}
                                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                        title={isMaxSubSpacesReached ? "Subspace limit reached" : "Add Subspace"}
                                        disabled={isMaxSubSpacesReached}
                                    >
                                        <Plus size={14} />
                                        <span className="small">Sub</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => deleteSpace(space.id)}
                                    className="btn btn-link btn-sm p-1 text-danger"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Section Selection (Only for parent spaces) */}
                        {isParent && sections.length > 0 && (
                            <div className="border-top pt-3">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label className="form-label fw-medium mb-0 small">Menu Sections</label>
                                    <div className="d-flex gap-2">
                                        <button
                                            onClick={() => selectAllSections(space.id)}
                                            className="btn btn-link btn-sm p-0 text-muted small"
                                        >
                                            Select All
                                        </button>
                                        <span className="text-muted">|</span>
                                        <button
                                            onClick={() => clearAllSections(space.id)}
                                            className="btn btn-link btn-sm p-0 text-muted small"
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {sections.map((section) => {
                                        const isSelected = selectedSections.includes(section.id);
                                        return (
                                            <button
                                                key={section.id}
                                                onClick={() => toggleSection(space.id, section.id)}
                                                className={`btn btn-sm ${isSelected ? 'text-white' : 'btn-outline-secondary'}`}
                                                style={isSelected ? { backgroundColor: THEME_COLOR, borderColor: THEME_COLOR } : {}}
                                            >
                                                {section.name}
                                            </button>
                                        );
                                    })}
                                </div>
                                <small className="text-muted d-block mt-2">
                                    {selectedSections.length === 0
                                        ? 'No sections selected'
                                        : `${selectedSections.length} section${selectedSections.length > 1 ? 's' : ''} selected`}
                                </small>
                            </div>
                        )}

                        {/* Inheritance info for subspaces */}
                        {!isParent && (
                            <div className="alert alert-info d-flex align-items-center gap-2 py-2 px-3 mb-0 mt-2">
                                <span className="small">Inherits sections from parent space</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Render children if expanded */}
                {isExpanded && children.map(child => renderSpace(child, level + 1))}
            </div>
        );
    };

    return (
        <div className="h-100 overflow-auto">
            <div className="container py-5" style={{ maxWidth: '900px' }}>
                <div className="mb-5">
                    <h1 className="h3 mb-2">Create Space</h1>
                    <p className="text-muted">Add spaces and select which menu sections appear in each</p>
                    <div className="alert alert-info d-flex align-items-start gap-2 mt-3">
                        <div className="small">
                            <strong>Parent Spaces & Subspaces:</strong> Create main spaces and select their menu sections.
                            Add subspaces under them - they'll automatically inherit the parent's sections.
                            You'll arrange section order in the next step.
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-3 mb-4">
                    <button
                        onClick={() => addSpace()}
                        className="btn text-white d-inline-flex align-items-center gap-2"
                        style={{ backgroundColor: THEME_COLOR, borderColor: THEME_COLOR, opacity: isMaxSpacesReached ? 0.6 : 1 }}
                        disabled={isMaxSpacesReached}
                    >
                        <Plus size={16} />
                        Add Space
                    </button>
                    {isMaxSpacesReached && (
                        <span className="text-danger small fw-bold">
                            Plan Limit Reached: Max {spaceLimit.limit} spaces
                        </span>
                    )}
                </div>

                <div>
                    {parentSpaces.map(space => renderSpace(space))}

                    {parentSpaces.length === 0 && (
                        <div className="text-center py-5 bg-light border border-2 border-dashed rounded-3">
                            <p className="text-muted mb-0">No spaces created yet</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SpaceCreation;
